import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../App";

const TenantsPage = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantStats, setTenantStats] = useState({});
  const [tenantUsers, setTenantUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteCheckbox, setDeleteCheckbox] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  const [workflows, setWorkflows] = useState({ onboarding: [], offboarding: [] });
  const [showWorkflowStepModal, setShowWorkflowStepModal] = useState(false);
  const [workflowType, setWorkflowType] = useState('onboarding');
  const [pendingDeletionTenants, setPendingDeletionTenants] = useState([]);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteMode, setDeleteMode] = useState('soft'); // 'soft' or 'permanent'

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Erro ao carregar tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantStats = async (tenantId) => {
    try {
      const response = await api.get(`/tenants/${tenantId}/stats`);
      setTenantStats(prev => ({ ...prev, [tenantId]: response.data }));
    } catch (error) {
      console.error('Error fetching tenant stats:', error);
    }
  };

  const fetchTenantUsers = async (tenantId) => {
    try {
      const response = await api.get(`/tenants/${tenantId}/users`);
      setTenantUsers(response.data);
    } catch (error) {
      console.error('Error fetching tenant users:', error);
    }
  };

  const fetchWorkflows = async (tenantId) => {
    try {
      const response = await api.get(`/tenants/${tenantId}/workflows`);
      setWorkflows(response.data);
    } catch (error) {
      console.error('Error fetching workflows:', error);
    }
  };

  const fetchPendingDeletionTenants = async () => {
    try {
      const response = await api.get('/tenants/deletion/pending');
      setPendingDeletionTenants(response.data);
    } catch (error) {
      console.error('Error fetching pending deletion tenants:', error);
    }
  };

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchTenants();
      fetchPendingDeletionTenants();
    }
  }, [user]);

  useEffect(() => {
    if (selectedTenant) {
      fetchTenantStats(selectedTenant.tenant_id);
      fetchTenantUsers(selectedTenant.tenant_id);
      fetchWorkflows(selectedTenant.tenant_id);
    }
  }, [selectedTenant]);

  const handleSelectTenant = (tenant) => {
    setSelectedTenant(tenant);
  };

  const handleEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setShowEditModal(true);
  };

  const handleUpdateTenant = async (tenantId, data) => {
    try {
      await api.put(`/tenants/${tenantId}`, data);
      toast.success('Tenant atualizado com sucesso');
      fetchTenants();
      setShowEditModal(false);
      if (selectedTenant?.tenant_id === tenantId) {
        setSelectedTenant({ ...selectedTenant, ...data });
      }
    } catch (error) {
      console.error('Error updating tenant:', error);
      toast.error('Erro ao atualizar tenant');
    }
  };

  const openDeleteModal = async (tenant) => {
    setTenantToDelete(tenant);
    setDeleteConfirmText('');
    setDeleteCheckbox(false);
    setDeleteReason('');
    setDeleteMode('soft');
    // Fetch fresh stats for deletion preview
    try {
      const response = await api.get(`/tenants/${tenant.tenant_id}/stats`);
      setTenantStats(prev => ({ ...prev, [tenant.tenant_id]: response.data }));
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
    setShowDeleteModal(true);
  };

  const handleScheduleDeletion = async () => {
    if (!tenantToDelete) return;
    if (deleteMode === 'soft' && !deleteReason.trim()) {
      toast.error('Indique um motivo para a eliminação');
      return;
    }
    if (!deleteCheckbox) {
      toast.error('Deve confirmar a checkbox');
      return;
    }

    setDeleting(true);
    try {
      const response = await api.post(`/tenants/${tenantToDelete.tenant_id}/schedule-deletion`, {
        reason: deleteReason
      });
      toast.success(response.data.message);
      setShowDeleteModal(false);
      setTenantToDelete(null);
      setDeleteReason('');
      setDeleteCheckbox(false);
      fetchTenants();
      fetchPendingDeletionTenants();
      if (selectedTenant?.tenant_id === tenantToDelete.tenant_id) {
        setSelectedTenant(null);
      }
    } catch (error) {
      console.error('Error scheduling deletion:', error);
      toast.error(error.response?.data?.detail || 'Erro ao agendar eliminação');
    } finally {
      setDeleting(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!tenantToDelete) return;
    if (deleteConfirmText !== tenantToDelete.name) {
      toast.error('Nome do tenant não corresponde');
      return;
    }
    if (!deleteCheckbox) {
      toast.error('Deve confirmar a checkbox');
      return;
    }

    setDeleting(true);
    try {
      const response = await api.delete(`/tenants/${tenantToDelete.tenant_id}/force?confirm_permanent_deletion=true`);
      toast.success(response.data.message);
      setShowDeleteModal(false);
      setTenantToDelete(null);
      setDeleteConfirmText('');
      setDeleteCheckbox(false);
      fetchTenants();
      fetchPendingDeletionTenants();
      if (selectedTenant?.tenant_id === tenantToDelete.tenant_id) {
        setSelectedTenant(null);
      }
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error(error.response?.data?.detail || 'Erro ao eliminar tenant');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreTenant = async (tenantId) => {
    if (!window.confirm('Deseja restaurar este tenant? Os utilizadores serão reativados.')) {
      return;
    }
    try {
      const response = await api.post(`/tenants/${tenantId}/restore`);
      toast.success(response.data.message);
      fetchTenants();
      fetchPendingDeletionTenants();
    } catch (error) {
      console.error('Error restoring tenant:', error);
      toast.error(error.response?.data?.detail || 'Erro ao restaurar tenant');
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    // This is the soft-delete function (deactivate)
    if (!window.confirm('Tem a certeza que deseja desativar este tenant? Esta ação pode ser revertida.')) {
      return;
    }
    try {
      await api.put(`/tenants/${tenantId}/deactivate`);
      toast.success('Tenant desativado com sucesso');
      fetchTenants();
      if (selectedTenant?.tenant_id === tenantId) {
        setSelectedTenant(null);
      }
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error('Erro ao desativar tenant');
    }
  };

  const handleCreateTenant = async (data) => {
    try {
      await api.post('/tenants', data);
      toast.success('Tenant criado com sucesso');
      fetchTenants();
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast.error('Erro ao criar tenant');
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Acesso restrito a Super Admins</p>
      </div>
    );
  }

  const stats = selectedTenant ? tenantStats[selectedTenant.tenant_id] : null;

  return (
    <div data-testid="tenants-page" className="space-y-6 animate-fade-in">
      {/* Pending Deletion Alert */}
      {pendingDeletionTenants.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <h3 className="font-medium text-amber-800 dark:text-amber-300">
                {pendingDeletionTenants.length} Tenant(s) Pendente(s) de Eliminação
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                Os seguintes tenants serão permanentemente eliminados após 30 dias. Pode restaurá-los antes dessa data.
              </p>
              <div className="mt-3 space-y-2">
                {pendingDeletionTenants.map(tenant => (
                  <div key={tenant.tenant_id} className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border border-amber-200 dark:border-amber-700">
                    <div>
                      <span className="font-medium text-amber-900 dark:text-amber-200">{tenant.name}</span>
                      <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                        ({tenant.days_until_permanent_deletion || 30} dias restantes)
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRestoreTenant(tenant.tenant_id)}
                        className="px-3 py-1 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
                        data-testid={`restore-tenant-${tenant.tenant_id}`}
                      >
                        Restaurar
                      </button>
                      <button
                        onClick={() => openDeleteModal(tenant)}
                        className="px-3 py-1 text-sm rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors"
                        data-testid={`force-delete-${tenant.tenant_id}`}
                      >
                        Eliminar Agora
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Tenants</h1>
          <p className="text-muted-foreground">Administração de organizações na plataforma</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
          data-testid="create-tenant-btn"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novo Tenant
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Tenants List */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border bg-card">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold">Tenants ({tenants.length})</h2>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : tenants.length === 0 ? (
                <p className="p-4 text-center text-muted-foreground">Nenhum tenant encontrado</p>
              ) : (
                <div className="divide-y divide-border">
                  {tenants.map((tenant) => (
                    <button
                      key={tenant.tenant_id}
                      onClick={() => handleSelectTenant(tenant)}
                      className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${selectedTenant?.tenant_id === tenant.tenant_id ? 'bg-muted/50 border-l-4 border-l-primary' : ''}`}
                      data-testid={`tenant-row-${tenant.tenant_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{tenant.name}</p>
                          <p className="text-xs text-muted-foreground">{tenant.domain || tenant.tenant_id}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${tenant.is_active !== false ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' : 'bg-red-100 text-red-700 dark:bg-red-900/30'}`}>
                          {tenant.is_active !== false ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tenant Details */}
        <div className="lg:col-span-2">
          {!selectedTenant ? (
            <div className="rounded-xl border bg-card p-12 text-center">
              <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="text-muted-foreground">Selecione um tenant para ver detalhes</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Tenant Header */}
              <div className="rounded-xl border bg-card p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">{selectedTenant.name}</h2>
                    <p className="text-muted-foreground">{selectedTenant.domain || selectedTenant.tenant_id}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditTenant(selectedTenant)}
                      className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                      data-testid="edit-tenant-btn"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteTenant(selectedTenant.tenant_id)}
                      className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-sm font-medium hover:bg-amber-200 transition-colors"
                      data-testid="deactivate-tenant-btn"
                    >
                      Desativar
                    </button>
                    <button
                      onClick={() => openDeleteModal(selectedTenant)}
                      className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-sm font-medium hover:bg-red-200 transition-colors"
                      data-testid="delete-tenant-btn"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>

              {/* Tenant Stats */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="rounded-xl border bg-card p-4 text-center">
                    <p className="text-2xl font-bold text-blue-500">{stats.data_counts?.clients || 0}</p>
                    <p className="text-xs text-muted-foreground">Clientes</p>
                  </div>
                  <div className="rounded-xl border bg-card p-4 text-center">
                    <p className="text-2xl font-bold text-purple-500">{stats.data_counts?.suppliers || 0}</p>
                    <p className="text-xs text-muted-foreground">Fornecedores</p>
                  </div>
                  <div className="rounded-xl border bg-card p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-500">{stats.data_counts?.employees || 0}</p>
                    <p className="text-xs text-muted-foreground">Colaboradores</p>
                  </div>
                  <div className="rounded-xl border bg-card p-4 text-center">
                    <p className="text-2xl font-bold text-amber-500">{stats.data_counts?.offers || 0}</p>
                    <p className="text-xs text-muted-foreground">Ofertas</p>
                  </div>
                  <div className="rounded-xl border bg-card p-4 text-center">
                    <p className="text-2xl font-bold text-red-500">{stats.data_counts?.conflicts || 0}</p>
                    <p className="text-xs text-muted-foreground">Conflitos</p>
                  </div>
                  <div className="rounded-xl border bg-card p-4 text-center">
                    <p className="text-2xl font-bold">{stats.data_counts?.users || 0}</p>
                    <p className="text-xs text-muted-foreground">Utilizadores</p>
                  </div>
                </div>
              )}

              {/* Detail Tabs */}
              <div className="flex gap-2 border-b border-border pb-2">
                <button
                  onClick={() => setActiveDetailTab('overview')}
                  className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${activeDetailTab === 'overview' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Utilizadores
                </button>
                <button
                  onClick={() => setActiveDetailTab('workflows')}
                  className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${activeDetailTab === 'workflows' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="workflows-tab"
                >
                  Workflows
                </button>
              </div>

              {/* Tenant Users Tab */}
              {activeDetailTab === 'overview' && (
              <div className="rounded-xl border bg-card">
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold">Utilizadores do Tenant ({tenantUsers.length})</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenantUsers.map((u) => (
                        <tr key={u.user_id}>
                          <td className="font-medium">{u.name}</td>
                          <td className="text-muted-foreground">{u.email}</td>
                          <td>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.role === 'tenant_admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30' : u.role === 'colaborador' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30'}`}>
                              {u.role}
                            </span>
                          </td>
                          <td>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.is_active !== false ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' : 'bg-red-100 text-red-700 dark:bg-red-900/30'}`}>
                              {u.is_active !== false ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              {/* Workflows Tab */}
              {activeDetailTab === 'workflows' && (
                <WorkflowsEditor
                  tenantId={selectedTenant.tenant_id}
                  workflows={workflows}
                  onRefresh={() => fetchWorkflows(selectedTenant.tenant_id)}
                />
              )}

              {/* Offer Settings */}
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Configurações de Ofertas</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Valor Máximo</p>
                    <p className="font-medium">{selectedTenant.offer_settings?.max_offer_value || 50}€</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Aprovação Obrigatória Acima de</p>
                    <p className="font-medium">{selectedTenant.offer_settings?.offer_approval_required_above || 25}€</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Auto-Rejeição Acima de</p>
                    <p className="font-medium">{selectedTenant.offer_settings?.auto_reject_offers_above || 500}€</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <TenantModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateTenant}
        />
      )}

      {/* Edit Tenant Modal */}
      {showEditModal && editingTenant && (
        <TenantModal
          tenant={editingTenant}
          onClose={() => setShowEditModal(false)}
          onSave={(data) => handleUpdateTenant(editingTenant.tenant_id, data)}
        />
      )}

      {/* Delete Tenant Confirmation Modal */}
      {showDeleteModal && tenantToDelete && (
        <DeleteTenantModal
          tenant={tenantToDelete}
          stats={tenantStats[tenantToDelete.tenant_id]}
          confirmText={deleteConfirmText}
          setConfirmText={setDeleteConfirmText}
          checkbox={deleteCheckbox}
          setCheckbox={setDeleteCheckbox}
          deleteMode={deleteMode}
          setDeleteMode={setDeleteMode}
          deleteReason={deleteReason}
          setDeleteReason={setDeleteReason}
          onScheduleDeletion={handleScheduleDeletion}
          onPermanentDelete={handlePermanentDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setTenantToDelete(null);
            setDeleteConfirmText('');
            setDeleteCheckbox(false);
            setDeleteReason('');
            setDeleteMode('soft');
          }}
          deleting={deleting}
        />
      )}
    </div>
  );
};

// Tenant Modal (Create/Edit)
const TenantModal = ({ tenant, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: tenant?.name || '',
    domain: tenant?.domain || '',
    logo_url: tenant?.logo_url || '',
    is_active: tenant?.is_active !== false,
    max_offer_value: tenant?.offer_settings?.max_offer_value || 50,
    offer_approval_required_above: tenant?.offer_settings?.offer_approval_required_above || 25,
    auto_reject_offers_above: tenant?.offer_settings?.auto_reject_offers_above || 500,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const data = {
      name: formData.name,
      domain: formData.domain,
      logo_url: formData.logo_url,
      is_active: formData.is_active,
      offer_settings: {
        max_offer_value: parseFloat(formData.max_offer_value),
        offer_approval_required_above: parseFloat(formData.offer_approval_required_above),
        auto_reject_offers_above: parseFloat(formData.auto_reject_offers_above),
      }
    };
    
    await onSave(data);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold">{tenant ? 'Editar Tenant' : 'Novo Tenant'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nome *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              required
              data-testid="tenant-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Domínio</label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="exemplo.com"
              data-testid="tenant-domain-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">URL do Logo</label>
            <input
              type="url"
              value={formData.logo_url}
              onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="https://..."
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded border-input"
            />
            <label htmlFor="is_active" className="text-sm">Tenant Ativo</label>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="text-sm font-semibold mb-3">Configurações de Ofertas</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm mb-1">Valor Máximo de Oferta (€)</label>
                <input
                  type="number"
                  value={formData.max_offer_value}
                  onChange={(e) => setFormData({ ...formData, max_offer_value: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Aprovação Obrigatória Acima de (€)</label>
                <input
                  type="number"
                  value={formData.offer_approval_required_above}
                  onChange={(e) => setFormData({ ...formData, offer_approval_required_above: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Auto-Rejeição Acima de (€)</label>
                <input
                  type="number"
                  value={formData.auto_reject_offers_above}
                  onChange={(e) => setFormData({ ...formData, auto_reject_offers_above: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !formData.name}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="save-tenant-btn"
            >
              {saving ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Tenant Confirmation Modal
const DeleteTenantModal = ({ 
  tenant, stats, confirmText, setConfirmText, checkbox, setCheckbox,
  deleteMode, setDeleteMode, deleteReason, setDeleteReason,
  onScheduleDeletion, onPermanentDelete, onClose, deleting 
}) => {
  if (!tenant) return null;
  
  const dataCounts = stats?.data_counts || {};
  const totalRecords = Object.values(dataCounts).reduce((a, b) => a + b, 0);
  const isPendingDeletion = tenant.deletion_scheduled;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-tenant-modal">
      <div className="bg-card rounded-xl border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className={`p-6 border-b border-border ${isPendingDeletion ? 'bg-red-50 dark:bg-red-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isPendingDeletion ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
              <svg className={`w-6 h-6 ${isPendingDeletion ? 'text-red-600' : 'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h2 className={`text-xl font-semibold ${isPendingDeletion ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                {isPendingDeletion ? 'Eliminar Tenant Permanentemente' : 'Eliminar Tenant'}
              </h2>
              <p className={`text-sm ${isPendingDeletion ? 'text-red-600/80 dark:text-red-400/80' : 'text-amber-600/80 dark:text-amber-400/80'}`}>
                {isPendingDeletion ? 'Esta ação é IRREVERSÍVEL' : 'Agendar eliminação com período de 30 dias para restauração'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Mode Selection (only if not pending deletion) */}
          {!isPendingDeletion && (
            <div className="space-y-3">
              <label className="block text-sm font-medium">Tipo de Eliminação</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteMode('soft')}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    deleteMode === 'soft' 
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' 
                      : 'border-border hover:border-amber-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">Com Backup</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Cria backup e permite restaurar em 30 dias
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteMode('permanent')}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    deleteMode === 'permanent' 
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                      : 'border-border hover:border-red-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span className="font-medium">Permanente</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Elimina imediatamente. Irreversível!
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Info Message based on mode */}
          <div className={`rounded-lg p-4 ${
            deleteMode === 'permanent' || isPendingDeletion
              ? 'bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              : 'bg-amber-100 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
          }`}>
            {deleteMode === 'soft' && !isPendingDeletion ? (
              <>
                <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                  O tenant <strong>&ldquo;{tenant.name}&rdquo;</strong> será marcado para eliminação.
                </p>
                <ul className="text-sm text-amber-700 dark:text-amber-400 mt-2 space-y-1 list-disc list-inside">
                  <li>Será criado um backup completo de todos os dados</li>
                  <li>Todos os utilizadores serão desativados</li>
                  <li>Pode restaurar nos próximos 30 dias</li>
                  <li>Após 30 dias, será eliminado permanentemente</li>
                </ul>
              </>
            ) : (
              <>
                <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                  Está prestes a eliminar <strong>PERMANENTEMENTE</strong> o tenant <strong>&ldquo;{tenant.name}&rdquo;</strong>.
                </p>
                <p className="text-sm text-red-700 dark:text-red-400 mt-2">
                  Esta ação não pode ser revertida. Todos os dados serão perdidos para sempre.
                </p>
              </>
            )}
          </div>

          {/* Data counts */}
          <div>
            <h3 className="font-medium mb-3">Dados {deleteMode === 'soft' ? 'que serão salvaguardados' : 'que serão eliminados'}:</h3>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="bg-muted/50 p-2 rounded text-center">
                <p className="font-bold text-lg">{dataCounts.clients || 0}</p>
                <p className="text-xs text-muted-foreground">Clientes</p>
              </div>
              <div className="bg-muted/50 p-2 rounded text-center">
                <p className="font-bold text-lg">{dataCounts.suppliers || 0}</p>
                <p className="text-xs text-muted-foreground">Fornecedores</p>
              </div>
              <div className="bg-muted/50 p-2 rounded text-center">
                <p className="font-bold text-lg">{dataCounts.employees || 0}</p>
                <p className="text-xs text-muted-foreground">Colaboradores</p>
              </div>
              <div className="bg-muted/50 p-2 rounded text-center">
                <p className="font-bold text-lg">{dataCounts.users || 0}</p>
                <p className="text-xs text-muted-foreground">Utilizadores</p>
              </div>
              <div className="bg-muted/50 p-2 rounded text-center">
                <p className="font-bold text-lg">{dataCounts.documents || 0}</p>
                <p className="text-xs text-muted-foreground">Documentos</p>
              </div>
              <div className="bg-muted/50 p-2 rounded text-center">
                <p className="font-bold text-lg">{dataCounts.equipment || 0}</p>
                <p className="text-xs text-muted-foreground">Equipamentos</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Total: <strong>{totalRecords}</strong> registos
            </p>
          </div>

          {/* Reason Input (only for soft delete) */}
          {deleteMode === 'soft' && !isPendingDeletion && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Motivo da Eliminação <span className="text-red-500">*</span>
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                placeholder="Indique o motivo da eliminação..."
                rows={2}
                data-testid="delete-reason-input"
              />
            </div>
          )}

          {/* Confirmation Input (only for permanent delete) */}
          {(deleteMode === 'permanent' || isPendingDeletion) && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Para confirmar, escreva o nome do tenant: <strong className="text-red-600">{tenant.name}</strong>
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-red-300 bg-background focus:border-red-500 focus:ring-red-500"
                placeholder="Escreva o nome para confirmar"
                data-testid="delete-confirm-input"
              />
            </div>
          )}

          {/* Checkbox Confirmation */}
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${
            deleteMode === 'permanent' || isPendingDeletion
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
          }`}>
            <input
              type="checkbox"
              id="delete-confirm-checkbox"
              checked={checkbox}
              onChange={(e) => setCheckbox(e.target.checked)}
              className={`mt-1 rounded ${deleteMode === 'permanent' || isPendingDeletion ? 'border-red-400 text-red-600' : 'border-amber-400 text-amber-600'}`}
              data-testid="delete-confirm-checkbox"
            />
            <label htmlFor="delete-confirm-checkbox" className={`text-sm ${
              deleteMode === 'permanent' || isPendingDeletion ? 'text-red-800 dark:text-red-300' : 'text-amber-800 dark:text-amber-300'
            }`}>
              {deleteMode === 'soft' && !isPendingDeletion ? (
                <>
                  Compreendo que o tenant será desativado, todos os utilizadores perderão acesso, e que tenho <strong>30 dias</strong> para restaurar antes da eliminação permanente.
                </>
              ) : (
                <>
                  Compreendo que esta ação é <strong>IRREVERSÍVEL</strong> e que todos os dados do tenant serão <strong>PERMANENTEMENTE ELIMINADOS</strong>.
                </>
              )}
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              disabled={deleting}
            >
              Cancelar
            </button>
            {deleteMode === 'soft' && !isPendingDeletion ? (
              <button
                onClick={onScheduleDeletion}
                disabled={deleting || !checkbox || !deleteReason.trim()}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="schedule-delete-btn"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    A processar...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Agendar Eliminação (30 dias)
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={onPermanentDelete}
                disabled={deleting || confirmText !== tenant.name || !checkbox}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    A eliminar...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Eliminar Permanentemente
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Workflows Editor Component
const WorkflowsEditor = ({ tenantId, workflows, onRefresh }) => {
  const [activeWorkflow, setActiveWorkflow] = useState('onboarding');
  const [showAddStepModal, setShowAddStepModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const steps = workflows[activeWorkflow] || [];

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Tem a certeza que deseja remover esta etapa?')) return;
    
    try {
      await api.delete(`/tenants/${tenantId}/workflows/${activeWorkflow}/step/${stepId}`);
      toast.success('Etapa removida');
      onRefresh();
    } catch (error) {
      toast.error('Erro ao remover etapa');
    }
  };

  const handleAddStep = async (stepData) => {
    try {
      await api.post(`/tenants/${tenantId}/workflows/${activeWorkflow}/step`, stepData);
      toast.success('Etapa adicionada');
      onRefresh();
      setShowAddStepModal(false);
    } catch (error) {
      toast.error('Erro ao adicionar etapa');
    }
  };

  const handleReorderSteps = async (newOrder) => {
    const reorderedSteps = newOrder.map((stepId, idx) => {
      const step = steps.find(s => s.step_id === stepId);
      return { ...step, order: idx + 1 };
    });
    
    setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}/workflows/${activeWorkflow}`, { steps: reorderedSteps });
      toast.success('Ordem atualizada');
      onRefresh();
    } catch (error) {
      toast.error('Erro ao reordenar');
    } finally {
      setSaving(false);
    }
  };

  const moveStep = (idx, direction) => {
    const newSteps = [...steps];
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= steps.length) return;
    
    [newSteps[idx], newSteps[newIdx]] = [newSteps[newIdx], newSteps[idx]];
    handleReorderSteps(newSteps.map(s => s.step_id));
  };

  const categoryColors = {
    IT: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30',
    HR: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30',
    Training: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30',
    Facilities: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30',
    Team: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30',
    Other: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30'
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveWorkflow('onboarding')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeWorkflow === 'onboarding' ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            data-testid="onboarding-workflow-btn"
          >
            🚀 Onboarding
          </button>
          <button
            onClick={() => setActiveWorkflow('offboarding')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeWorkflow === 'offboarding' ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            data-testid="offboarding-workflow-btn"
          >
            👋 Offboarding
          </button>
        </div>
        <button
          onClick={() => setShowAddStepModal(true)}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          data-testid="add-workflow-step-btn"
        >
          + Adicionar Etapa
        </button>
      </div>

      <div className="rounded-xl border bg-card">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold">
            {activeWorkflow === 'onboarding' ? 'Etapas de Onboarding' : 'Etapas de Offboarding'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {activeWorkflow === 'onboarding' 
              ? 'Define as etapas para integração de novos colaboradores'
              : 'Define as etapas para saída de colaboradores'}
          </p>
        </div>

        <div className="p-4 space-y-2">
          {steps.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Sem etapas definidas</p>
              <button
                onClick={() => setShowAddStepModal(true)}
                className="mt-2 text-primary hover:underline"
              >
                Adicionar primeira etapa
              </button>
            </div>
          ) : (
            steps.sort((a, b) => a.order - b.order).map((step, idx) => (
              <div
                key={step.step_id}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                data-testid={`workflow-step-${step.step_id}`}
              >
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveStep(idx, 'up')}
                    disabled={idx === 0 || saving}
                    className="p-1 rounded hover:bg-background disabled:opacity-30"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => moveStep(idx, 'down')}
                    disabled={idx === steps.length - 1 || saving}
                    className="p-1 rounded hover:bg-background disabled:opacity-30"
                  >
                    ▼
                  </button>
                </div>
                
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                  {idx + 1}
                </div>
                
                <div className="flex-1">
                  <p className="font-medium">{step.title}</p>
                  {step.description && <p className="text-xs text-muted-foreground">{step.description}</p>}
                </div>
                
                <span className={`px-2 py-1 rounded text-xs font-medium ${categoryColors[step.category] || categoryColors.Other}`}>
                  {step.category}
                </span>
                
                {step.required && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30">
                    Obrigatório
                  </span>
                )}
                
                <button
                  onClick={() => handleDeleteStep(step.step_id)}
                  className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Step Modal */}
      {showAddStepModal && (
        <AddWorkflowStepModal
          workflowType={activeWorkflow}
          onClose={() => setShowAddStepModal(false)}
          onSave={handleAddStep}
        />
      )}
    </div>
  );
};

// Add Workflow Step Modal
const AddWorkflowStepModal = ({ workflowType, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'HR',
    required: true,
    responsible_role: ''
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border shadow-lg w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold">Adicionar Etapa de {workflowType === 'onboarding' ? 'Onboarding' : 'Offboarding'}</h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Título da Etapa *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              placeholder="Ex: Entrega de equipamento"
              data-testid="step-title-input"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Descrição</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              rows={2}
              placeholder="Descrição detalhada da etapa"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Categoria</label>
              <select
                value={formData.category}
                onChange={e => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              >
                <option value="IT">IT</option>
                <option value="HR">HR</option>
                <option value="Training">Formação</option>
                <option value="Facilities">Instalações</option>
                <option value="Team">Equipa</option>
                <option value="Other">Outro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Responsável</label>
              <input
                type="text"
                value={formData.responsible_role}
                onChange={e => setFormData({...formData, responsible_role: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                placeholder="Ex: IT Manager"
              />
            </div>
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.required}
              onChange={e => setFormData({...formData, required: e.target.checked})}
              className="rounded border-input"
            />
            <span className="text-sm">Etapa obrigatória</span>
          </label>
        </div>
        <div className="p-6 border-t border-border flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border hover:bg-muted">
            Cancelar
          </button>
          <button
            onClick={() => onSave(formData)}
            disabled={!formData.title}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50"
            data-testid="save-step-btn"
          >
            Adicionar
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantsPage;
